(function () {
  'use strict';

  const { gsap } = window;
  const { iman, BaseCarousel, easePower3 } = window.Universe;

  /* -- AdvancedSlideshow ---------------------------------------------- */

  class AdvancedSlideshow {
    constructor(section) {
      this.onCarouselProgress = () => {
        let totalProgress = Math.max(0, Math.min(1, parseFloat(this.instance.scrollProgress().toFixed(3))));

        const totalSlides = this.snapList.length;

        const selectedScrollSnap = this.instance.selectedScrollSnap();
        const previousScrollSnap = this.instance.previousScrollSnap();

        const nextSnap = this.snapList[selectedScrollSnap] / this.snapList[totalSlides - 1];
        const prevSnap = this.snapList[previousScrollSnap] / this.snapList[totalSlides - 1];

        let percentage = Math.max(0, Math.min(100, Math.abs((totalProgress - prevSnap) / (nextSnap - prevSnap))));
        if (isNaN(percentage)) {
          percentage = 1;
        }

        this.setGroupOpacity(this.DOM.bgs, selectedScrollSnap, previousScrollSnap, percentage);
        if (this.DOM.prevDividers.length) {
          this.setGroupOpacity(this.DOM.prevDividers, selectedScrollSnap, previousScrollSnap, percentage);
        }
        if (this.DOM.nextDividers.length) {
          this.setGroupOpacity(this.DOM.nextDividers, selectedScrollSnap, previousScrollSnap, percentage);
        }
      };

      this.onCarouselSelect = () => {
        const prevIndex = this.activeIndex;
        this.activeIndex = this.instance.selectedScrollSnap();

        if (prevIndex === this.activeIndex) {
          this.setBottomGroupsInteractivity();
          this.updateNavigationState();
          return;
        }

        const direction = this.activeIndex > prevIndex ? 'next' : 'prev';
        const prevImage = this.DOM.images[prevIndex];
        const currentImage = this.DOM.images[this.activeIndex];
        const duration = 1.15;

        const prevSlide = this.DOM.slides[prevIndex];
        if (prevSlide) {
          this.DOM.head.classList.remove('color-' + prevSlide.dataset.colorScheme);
          this.DOM.navigationItems.forEach((item) => {
            item.classList.remove('color-' + prevSlide.dataset.colorScheme);
          });
        }
        const newActiveSlide = this.DOM.slides[this.activeIndex];
        if (newActiveSlide) {
          this.DOM.head.classList.add('color-' + newActiveSlide.dataset.colorScheme);
          this.DOM.navigationItems.forEach((item) => {
            item.classList.add('color-' + newActiveSlide.dataset.colorScheme);
          });
        }

        const defaults = {
          duration: duration,
          ease: easePower3,
        };

        const bottomTl = gsap.timeline({ defaults: defaults });
        bottomTl.to(
          this.DOM.bottomGroups,
          {
            y: (idx) => (idx - this.activeIndex) * 125,
            yPercent: (idx) => (idx - this.activeIndex) * 125,
          },
          0,
        );

        const titlesTl = gsap.timeline({ defaults: defaults });
        this.DOM.titles.forEach((title, idx) => {
          titlesTl.to(
            title.children,
            {
              x: () => (idx - this.activeIndex) * 100,
              xPercent: () => (idx - this.activeIndex) * 100,
              stagger: 0.1,
            },
            0,
          );
        });
        titlesTl.to(
          this.DOM.titles,
          {
            x: (idx) => (idx - this.activeIndex) * 100,
            xPercent: (idx) => (idx - this.activeIndex) * 100,
          },
          0,
        );

        const imageDefaults = { duration: duration - 0.2, ease: easePower3 };
        const imagesTl = gsap.timeline({ defaults: imageDefaults });
        imagesTl
          .to(prevImage, {
            clipPath:
              direction === 'next'
                ? 'polygon(0% 0%, 0% 0%, 0% 100%, 0% 100%)'
                : 'polygon(100% 0%, 100% 0%, 100% 100%, 100% 100%)',
          })
          .to(
            currentImage,
            {
              clipPath:
                direction === 'next'
                  ? 'polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)'
                  : 'polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)',
            },
            '-=' + (duration - 0.2),
          );

        this.setBottomGroupsInteractivity();
        this.updateNavigationState();
      };

      this.onCarouselPrev = () => {
        this.stopAutoplay();
        this.instance.scrollPrev();
      };

      this.onCarouselNext = () => {
        this.stopAutoplay();
        this.instance.scrollNext();
      };

      this.init(section);
    }

    setup() {
      if (this.DOM.slides.length) {
        const hasAutoplay = 'autoplay' in this.DOM.section.dataset;
        const delay = parseFloat(this.DOM.section.dataset.autoplayDelay) || 5;

        this.carousel = new BaseCarousel({
          wrap: this.DOM.section,
          carousel: this.DOM.carousel,
          carouselOptions: {
            align: 'center',
            containScroll: 'trimSnaps',
            duration: 35,
          },
          withAutoPlay: hasAutoplay,
          autoPlayDelay: delay * 1000,
          onReady: () => {
            this.instance = this.carousel.instance;

            this.DOM.carouselPrev.forEach((btn) => {
              btn.addEventListener('click', this.onCarouselPrev);
            });
            this.DOM.carouselNext.forEach((btn) => {
              btn.addEventListener('click', this.onCarouselNext);
            });

            this.snapList = this.instance.scrollSnapList();
            this.instance
              .on('init', this.onCarouselProgress)
              .on('reInit', this.onCarouselProgress)
              .on('scroll', this.onCarouselProgress)
              .on('select', this.onCarouselSelect)
              .on('slideFocus', this.onCarouselProgress);

            this.setupLinkSlides();
            this.setBottomGroupsInteractivity();
            this.updateNavigationState();
          },
        });
      }
    }

    setupLinkSlides() {
      const linkedSlides = Array.from(this.DOM.slides).filter((s) => s.tagName === 'A');
      if (!linkedSlides.length) return;

      const DRAG_THRESHOLD = 5;
      let dragStartX = 0;
      let dragStartY = 0;
      let hasDragged = false;

      this.onSlidePointerDown = (e) => {
        dragStartX = e.clientX;
        dragStartY = e.clientY;
        hasDragged = false;
      };

      this.onSlidePointerMove = (e) => {
        if (Math.abs(e.clientX - dragStartX) > DRAG_THRESHOLD || Math.abs(e.clientY - dragStartY) > DRAG_THRESHOLD) {
          hasDragged = true;
        }
      };

      this.onSlideLinkClick = (e) => {
        if (hasDragged) e.preventDefault();
      };

      this.DOM.carousel.addEventListener('pointerdown', this.onSlidePointerDown);
      this.DOM.carousel.addEventListener('pointermove', this.onSlidePointerMove);
      linkedSlides.forEach((slide) => slide.addEventListener('click', this.onSlideLinkClick));
      this._linkedSlides = linkedSlides;
    }

    resize() {
      if (this.DOM) {
        this.setBottomGroupsInteractivity();
      }
    }

    destroy() {
      if (this.DOM) {
        this.DOM.carouselPrev.forEach((btn) => {
          btn.removeEventListener('click', this.onCarouselPrev);
        });
        this.DOM.carouselNext.forEach((btn) => {
          btn.removeEventListener('click', this.onCarouselNext);
        });

        if (this._linkedSlides) {
          this.DOM.carousel.removeEventListener('pointerdown', this.onSlidePointerDown);
          this.DOM.carousel.removeEventListener('pointermove', this.onSlidePointerMove);
          this._linkedSlides.forEach((slide) => slide.removeEventListener('click', this.onSlideLinkClick));
          this._linkedSlides = undefined;
        }

        if (this.instance) {
          this.instance
            .off('init', this.onCarouselProgress)
            .off('reInit', this.onCarouselProgress)
            .off('scroll', this.onCarouselProgress)
            .off('select', this.onCarouselSelect)
            .off('slideFocus', this.onCarouselProgress);
        }

        if (this.carousel) {
          this.carousel.destroy();
          this.carousel = undefined;
        }

        this.instance = undefined;
        this.DOM = undefined;
      }
    }

    init(section) {
      if (!section) return;

      this.DOM = {};
      this.DOM.section = section;
      this.sectionId = section.closest('.shopify-section')?.id || null;
      this.DOM.carousel = this.DOM.section.querySelector('.js-advanced-hero-slideshow');
      this.DOM.slides = this.DOM.section.querySelectorAll('.js-advanced-hero-slideshow-slide');
      this.DOM.bgs = this.DOM.section.querySelectorAll('.js-advanced-hero-slideshow-bg');
      this.DOM.head = this.DOM.section.querySelector('.js-advanced-hero-slideshow-head');
      this.DOM.navigationItems = this.DOM.section.querySelectorAll('.js-advanced-hero-slideshow-navigation-item');
      this.DOM.images = this.DOM.section.querySelectorAll('.js-advanced-hero-slideshow-image');
      this.DOM.titles = this.DOM.section.querySelectorAll('.js-advanced-hero-slideshow-main-title');
      this.DOM.bottom = this.DOM.section.querySelector('.js-advanced-hero-slideshow-bottom');
      this.DOM.bottomGroups = this.DOM.section.querySelectorAll('.js-advanced-hero-slideshow-bottom-group');
      this.DOM.mobileNav = this.DOM.section.querySelector('.js-advanced-hero-slideshow-mobile-nav');

      this.DOM.carouselPrev = this.DOM.section.querySelectorAll('.js-carousel-prev');
      this.DOM.carouselNext = this.DOM.section.querySelectorAll('.js-carousel-next');

      const topDividersContainer = document.querySelector('.js-advanced-hero-slideshow-top-dividers');
      this.DOM.prevDividers = topDividersContainer
        ? topDividersContainer.querySelectorAll('.js-advanced-hero-slideshow-divider')
        : [];
      const bottomDividersContainer = document.querySelector('.js-advanced-hero-slideshow-bottom-dividers');
      this.DOM.nextDividers = bottomDividersContainer
        ? bottomDividersContainer.querySelectorAll('.js-advanced-hero-slideshow-divider')
        : [];

      this.activeIndex = 0;

      this.setup();
    }

    stopAutoplay() {
      if (!this.instance) return;
      const autoplay = this.instance.plugins().autoplay;
      if (autoplay) autoplay.stop();
    }

    setGroupOpacity(elements, selected, prev, percentage) {
      elements.forEach((element, i) => {
        element.style.opacity = i == selected ? percentage : i == prev ? 1 - percentage : 0;
      });
    }

    setBottomGroupsInteractivity() {
      if (!this.DOM || !this.DOM.bottomGroups || !this.DOM.bottomGroups.length) return;

      const supportsInert = 'inert' in HTMLElement.prototype;

      this.DOM.bottomGroups.forEach((group, i) => {
        const enable = i === this.activeIndex;

        group.style.pointerEvents = enable ? 'auto' : 'none';
        group.setAttribute('aria-hidden', enable ? 'false' : 'true');

        if (supportsInert) {
          group.inert = !enable;
        } else {
          const focusables = group.querySelectorAll('a, button, input, select, textarea, [tabindex]');
          focusables.forEach((el) => {
            if (enable) {
              if (el.hasAttribute('data-prev-tabindex')) {
                const prev = el.getAttribute('data-prev-tabindex');
                if (prev === '') el.removeAttribute('tabindex');
                else el.setAttribute('tabindex', prev);
                el.removeAttribute('data-prev-tabindex');
              } else {
                if (el.getAttribute('tabindex') === '-1') el.removeAttribute('tabindex');
              }
            } else {
              if (!el.hasAttribute('data-prev-tabindex')) {
                el.setAttribute('data-prev-tabindex', el.getAttribute('tabindex') || '');
              }
              el.setAttribute('tabindex', '-1');
            }
          });
        }
      });
    }

    updateNavigationState() {
      if (!this.instance) return;

      const canScrollPrev = this.instance.canScrollPrev();
      const canScrollNext = this.instance.canScrollNext();

      this.DOM.carouselPrev.forEach((btn) => {
        btn.setAttribute('aria-disabled', !canScrollPrev);
      });

      this.DOM.carouselNext.forEach((btn) => {
        btn.setAttribute('aria-disabled', !canScrollNext);
      });
    }
  }

  /* -- AdvancedSlideshows --------------------------------------------- */

  class AdvancedSlideshows {
    constructor() {
      this.init();
    }

    setup() {
      this.DOM.sections.forEach((section) => {
        this.carousels.push(new AdvancedSlideshow(section));
      });
    }

    resize() {
      if (this.DOM) {
        this.carousels.forEach((carousel) => {
          carousel.resize();
        });
      }
    }

    reinit() {
      this.destroy();
      this.init();
    }

    reinitSection(sectionEl) {
      this.destroySection(sectionEl);

      if (!this.DOM) {
        this.DOM = {};
        this.carousels = [];
      }

      const newSections = sectionEl.querySelectorAll('.js-advanced-hero-slideshow-section');
      for (let i = 0; i < newSections.length; i++) {
        this.carousels.push(new AdvancedSlideshow(newSections[i]));
      }

      this.DOM.sections = document.querySelectorAll('.js-advanced-hero-slideshow-section');
    }

    destroySection(sectionEl) {
      if (!this.carousels) return;

      const targetId = sectionEl.id || sectionEl.closest('.shopify-section')?.id;
      if (!targetId) return;

      for (let i = this.carousels.length - 1; i >= 0; i--) {
        const carousel = this.carousels[i];
        if (carousel.sectionId === targetId) {
          carousel.destroy();
          this.carousels.splice(i, 1);
        }
      }
    }

    onBlockSelect(blockEl) {
      for (let i = 0; i < this.carousels.length; i++) {
        const carousel = this.carousels[i];
        if (carousel.DOM && carousel.DOM.section.contains(blockEl)) {
          const slide = blockEl.closest('.js-advanced-hero-slideshow-slide');
          if (!slide) break;

          const slideIndex = Array.from(carousel.DOM.slides).indexOf(slide);
          if (slideIndex !== -1 && carousel.instance) {
            const autoplay = carousel.instance.plugins().autoplay;
            if (autoplay) autoplay.stop();
            carousel.instance.scrollTo(slideIndex);
          }
          break;
        }
      }
    }

    onBlockDeselect(blockEl) {
      for (let i = 0; i < this.carousels.length; i++) {
        const carousel = this.carousels[i];
        if (carousel.DOM && carousel.DOM.section.contains(blockEl)) {
          if (carousel.instance) {
            const autoplay = carousel.instance.plugins().autoplay;
            if (autoplay) autoplay.play();
          }
          break;
        }
      }
    }

    destroy() {
      if (this.DOM) {
        this.carousels.forEach((carousel) => {
          carousel.destroy();
        });

        this.carousels = [];
        this.DOM = undefined;
      }
    }

    init(container = document) {
      const sections = container.querySelectorAll('.js-advanced-hero-slideshow-section');

      if (sections.length) {
        this.DOM = {};
        this.DOM.sections = sections;

        this.carousels = [];

        this.setup();
      }
    }
  }

  /* -- Init ---------------------------------------------------------- */

  function init() {
    if (!iman.get('advancedSlideshows')) {
      iman.add('advancedSlideshows', new AdvancedSlideshows());
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.Universe.AdvancedSlideshows = AdvancedSlideshows;
})();
